import React, { forwardRef, type ForwardedRef } from 'react';
import { clsx } from 'clsx';
import './Checkbox.scss';

export interface ESSCheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Label for the checkbox */
  label?: React.ReactNode;
  /** Helper text to display below the checkbox */
  helperText?: string;
  /** Whether the checkbox is in an error state */
  isInvalid?: boolean;
  /** Error message to display when isInvalid is true */
  errorMessage?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, ESSCheckboxProps>(
  function Checkbox(
    { label, helperText, isInvalid, errorMessage, className, ...props },
    ref: ForwardedRef<HTMLInputElement>,
  ) {
    const checkboxClasses = clsx(
      'ess-checkbox',
      {
        'ess-checkbox--invalid': isInvalid,
      },
      className,
    );

    return (
      <label className={checkboxClasses}>
        <input
          ref={ref}
          type="checkbox"
          className="ess-checkbox__input"
          aria-invalid={isInvalid}
          {...props}
        />
        <span className="ess-checkbox__control">
          <svg
            className="ess-checkbox__icon"
            viewBox="0 0 18 18"
            aria-hidden="true"
          >
            <polyline
              points="1 9 7 14 17 4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        {label && <span className="ess-checkbox__label">{label}</span>}
        {(helperText || (isInvalid && errorMessage)) && (
          <span className="ess-checkbox__help-text">
            {isInvalid && errorMessage ? errorMessage : helperText}
          </span>
        )}
      </label>
    );
  },
);

Checkbox.displayName = 'ESSCheckbox';
